import { Autocomplete, Box, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { Moment } from "moment";
import { PdfButton } from "@/controls/buttons/PdfButton";
import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import type { CostCenterOption } from "@/pages/reports/summary-by-close-date/models/SummaryByCloseDate";

const costCenterLabel = (costCenter: CostCenterOption) =>
	costCenter.Description?.trim()
		? `${costCenter.Name} - ${costCenter.Description.trim()}`
		: costCenter.Name;

interface Props {
	costCenters: CostCenterOption[];
	costCenterId: number | null;
	onCostCenterChange: (value: number | null) => void;
	start: Moment;
	end: Moment;
	onStartChange: (date: Moment | null) => void;
	onEndChange: (date: Moment | null) => void;
	onReset: () => void;
	canReset: boolean;
	onExportPdf: () => void;
	canExportPdf: boolean;
	exportingPdf: boolean;
}

export const SummaryByDateRangeToolbar = ({
	costCenters,
	costCenterId,
	onCostCenterChange,
	start,
	end,
	onStartChange,
	onEndChange,
	onReset,
	canReset,
	onExportPdf,
	canExportPdf,
	exportingPdf,
}: Props) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
			px: 1.5,
			py: 0.75,
			flexWrap: "wrap",
		}}
	>
		<Autocomplete
			size="small"
			sx={{ flex: "0 0 350px" }}
			options={costCenters}
			value={costCenters.find((cc) => cc.Id === costCenterId) ?? null}
			getOptionLabel={costCenterLabel}
			isOptionEqualToValue={(option, selected) => option.Id === selected.Id}
			onChange={(_event, option) => onCostCenterChange(option?.Id ?? null)}
			disabled={costCenters.length === 0}
			autoHighlight
			renderOption={(props, option) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<li key={option.Id} {...rest}>
						{costCenterLabel(option)}
					</li>
				);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Cost Center"
					slotProps={{ inputLabel: { shrink: true } }}
					placeholder="Search cost center..."
				/>
			)}
		/>

		<Box sx={{ minWidth: 150, maxWidth: 170 }}>
			<DatePicker
				label="Start"
				format="MM/DD/YYYY"
				value={start}
				maxDate={end}
				onChange={onStartChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>
		<Box sx={{ minWidth: 150, maxWidth: 170 }}>
			<DatePicker
				label="Close"
				format="MM/DD/YYYY"
				value={end}
				minDate={start}
				onChange={onEndChange}
				slotProps={{ textField: { size: "small", fullWidth: true } }}
			/>
		</Box>

		<ResetFiltersButton onClick={onReset} disabled={!canReset} />

		<Box sx={{ flexGrow: 1, minWidth: 8 }} />

		<Box sx={{ width: 200 }}>
			<PdfButton
				handleExportToPdf={onExportPdf}
				disabled={!canExportPdf}
				loading={exportingPdf}
			/>
		</Box>
	</Box>
);
